import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import {
  valueToParts, partsToValue, nowParts, daysInMonth, zoneAbbrev,
} from '../../utils/dateTime';

/**
 * STUI date / time / date-time input. One box of segments (MM / DD / YYYY  hh : mm AM): click a
 * segment and type, or step it with the arrow keys or the spinner. The calendar button opens a
 * STUI month grid instead of the browser's picker, whose drop-downs cannot be styled.
 *
 *   <StuiDateTime mode="datetime" valueZone="utc" value={row.ScheduledAt} onChange={setAt} />
 *
 *   mode         'date' | 'time' | 'datetime'
 *   valueZone    what value holds (see utils/dateTime): 'utc' database timestamp, 'local' wall
 *                clock, 'none' plain date or time that is never shifted
 *   displayZone  'local' (default) or an IANA zone; what the person sees and types
 *   minuteStep   1 | 5 | 15: arrow / spinner step for minutes
 *   showZone     append the zone ("EDT")
 *   dirty        edited but not saved: the same highlight as .stui-field-input.dirty
 *
 * onChange gets the stored-format value once every segment is filled, and '' while any is
 * empty. AM/PM fills itself when an hour or minute is set; a calendar pick of today sets the
 * time to now, and of another day keeps the time (or takes now's when there is none). The page
 * never does zone arithmetic.
 */

const SEGMENTS = {
  date: ['month', 'day', 'year'],
  time: ['hour', 'minute', 'ampm'],
  datetime: ['month', 'day', 'year', 'hour', 'minute', 'ampm'],
};
const SPEC = {
  month:  { min: 1, max: 12, width: 2, placeholder: 'mm' },
  day:    { min: 1, max: 31, width: 2, placeholder: 'dd' },
  year:   { min: 1, max: 9999, width: 4, placeholder: 'yyyy' },
  hour:   { min: 1, max: 12, width: 2, placeholder: 'hh' },
  minute: { min: 0, max: 59, width: 2, placeholder: 'mm' },
};
const SEPARATOR_AFTER = { month: '/', day: '/', hour: ':' };
const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];

const EMPTY = { month: null, day: null, year: null, hour: null, minute: null, ampm: null };

const toSegs = (parts) => {
  if (!parts) return { ...EMPTY };
  const segs = { ...EMPTY, month: parts.month ?? null, day: parts.day ?? null, year: parts.year ?? null };
  if (parts.hour != null) {
    segs.hour = parts.hour % 12 || 12;
    segs.minute = parts.minute;
    segs.ampm = parts.hour < 12 ? 'AM' : 'PM';
  }
  return segs;
};

const fromSegs = (segs) => ({
  year: segs.year, month: segs.month,
  day: segs.year && segs.month ? Math.min(segs.day, daysInMonth(segs.year, segs.month)) : segs.day,
  hour: segs.hour == null ? null : (segs.hour % 12) + (segs.ampm === 'PM' ? 12 : 0),
  minute: segs.minute,
});

const segText = (key, v) => {
  if (v == null) return key === 'ampm' ? '--' : SPEC[key].placeholder;
  if (key === 'ampm') return v;
  return String(v).padStart(SPEC[key].width, '0');
};

const StuiDateTime = ({
  mode = 'datetime',
  value,
  onChange,
  valueZone = mode === 'datetime' ? 'local' : 'none',
  displayZone = 'local',
  minuteStep = 1,
  showZone = false,
  disabled = false,
  dirty = false,
  title,
}) => {
  const opts = useMemo(() => ({ mode, valueZone, displayZone }), [mode, valueZone, displayZone]);
  const order = SEGMENTS[mode];
  const [segs, setSegs] = useState(() => toSegs(valueToParts(value, opts)));
  const [calendarAt, setCalendarAt] = useState(null);    // { top, left } while open
  const [view, setView] = useState(null);                // { year, month } shown in the calendar
  const [compact, setCompact] = useState(false);         // box too narrow for spinner + padding
  const segRefs = useRef({});
  const boxRef = useRef(null);
  const calRef = useRef(null);
  const typed = useRef({ key: null, buf: '' });
  const activeSeg = useRef(null);
  const lastEmitted = useRef(value ?? '');

  // External value -> segments, skipping our own emission echoed back (a partial entry emits ''
  // and must not be wiped by it).
  useEffect(() => {
    if ((value ?? '') === lastEmitted.current) return;
    lastEmitted.current = value ?? '';
    setSegs(toSegs(valueToParts(value, opts)));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // A narrow box (a two-column form field) can't hold segments, spinner and calendar button.
  // Compact drops the spinner, tightens the padding and shows the calendar button only on hover or
  // focus, until the box is wide enough again; the full width is remembered so the switch back
  // can't flicker.
  const fullWidth = useRef(0);
  useEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return undefined;
    const fit = () => setCompact(was => {
      if (!was && box.scrollWidth > box.clientWidth) { fullWidth.current = box.scrollWidth; return true; }
      if (was && box.clientWidth >= fullWidth.current) return false;
      return was;
    });
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    return () => ro.disconnect();
  }, []);

  const commit = useCallback((input, changedKey) => {
    // Setting an hour or minute while AM/PM is still blank takes now's AM/PM, so a typed time
    // completes the value instead of silently waiting on a third segment.
    const next = (changedKey === 'hour' || changedKey === 'minute') && input[changedKey] != null && input.ampm == null
      ? { ...input, ampm: toSegs(nowParts(displayZone)).ampm }
      : input;
    setSegs(next);
    const complete = order.every(k => next[k] != null);
    const out = complete ? partsToValue(fromSegs(next), opts) : '';
    if (out !== lastEmitted.current) {
      lastEmitted.current = out;
      onChange?.(out);
    }
  }, [order, opts, onChange, displayZone]);

  const focusSeg = (key) => segRefs.current[key]?.focus();
  const neighbour = (key, dir) => order[order.indexOf(key) + dir];

  const nowSegs = () => toSegs(nowParts(displayZone));

  const step = (key, dir) => {
    const cur = segs[key];
    let next;
    if (key === 'ampm') {
      next = cur == null ? nowSegs().ampm : (cur === 'AM' ? 'PM' : 'AM');
    } else if (cur == null) {
      next = nowSegs()[key];
      if (key === 'minute') next -= next % minuteStep;
    } else {
      const { min, max } = SPEC[key];
      const inc = key === 'minute' ? minuteStep : 1;
      if (key === 'minute' && cur % inc !== 0) {
        next = dir > 0 ? cur - (cur % inc) + inc : cur - (cur % inc);
      } else {
        next = cur + dir * inc;
      }
      const span = max - min + 1;
      if (key !== 'year') next = ((next - min) % span + span) % span + min;
      else next = Math.min(max, Math.max(min, next));
    }
    commit({ ...segs, [key]: next }, key);
  };

  const onKeyDown = (key, e) => {
    if (disabled) return;
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      typed.current = { key: null, buf: '' };
      step(key, e.key === 'ArrowUp' ? 1 : -1);
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const n = neighbour(key, e.key === 'ArrowLeft' ? -1 : 1);
      if (n) focusSeg(n);
      return;
    }
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      typed.current = { key: null, buf: '' };
      commit({ ...segs, [key]: null });
      return;
    }
    if (key === 'ampm') {
      const k = e.key.toLowerCase();
      if (k === 'a' || k === 'p') {
        e.preventDefault();
        commit({ ...segs, ampm: k === 'a' ? 'AM' : 'PM' });
      }
      return;
    }
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const { min, max, width } = SPEC[key];
      const buf = (typed.current.key === key ? typed.current.buf : '') + e.key;
      const val = parseInt(buf, 10);
      const done = buf.length >= width || val * 10 > max;
      // A leading 0 waits for its second digit; anything else shows as typed.
      const shown = val === 0 && min > 0 ? null : Math.min(val, max);
      typed.current = done ? { key: null, buf: '' } : { key, buf };
      commit({ ...segs, [key]: done && val < min ? min : shown }, key);
      if (done) {
        const n = neighbour(key, 1);
        if (n) focusSeg(n);
      }
    }
  };

  // ── Calendar ─────────────────────────────────────────────────────────────
  const hasDate = mode !== 'time';
  // The spinner acts on the last focused segment; before any focus, the one people adjust most.
  const spinDefault = mode === 'date' ? 'day' : 'minute';
  const openCalendar = () => {
    if (disabled) return;
    const r = boxRef.current.getBoundingClientRect();
    const base = segs.year && segs.month ? segs : nowSegs();
    setView({ year: base.year, month: base.month });
    setCalendarAt({ top: r.bottom + 4, left: r.left });
  };
  const closeCalendar = () => setCalendarAt(null);

  useEffect(() => {
    if (!calendarAt) return undefined;
    const onDown = (e) => {
      if (calRef.current?.contains(e.target) || boxRef.current?.contains(e.target)) return;
      closeCalendar();
    };
    const onEsc = (e) => { if (e.key === 'Escape') closeCalendar(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [calendarAt]);

  // Picking today sets the time to now; picking another day keeps the time, or takes now's when
  // there is none yet, so a calendar pick always leaves a complete value.
  const pickDay = (year, month, day) => {
    let next = { ...segs, year, month, day };
    const t = nowParts(displayZone);
    if (mode === 'datetime' && ((year === t.year && month === t.month && day === t.day) || segs.hour == null)) {
      const now = toSegs({ ...t, minute: t.minute - (t.minute % minuteStep) });
      next = { ...next, hour: now.hour, minute: now.minute, ampm: now.ampm };
    }
    commit(next);
    closeCalendar();
  };

  // Today selects today and shows its month but leaves the calendar open, so a jump back from an
  // old date can still land on yesterday with one more click. Clear empties the value and closes.
  const pickToday = () => {
    const t = nowParts(displayZone);
    setView({ year: t.year, month: t.month });
    let next = { ...segs, year: t.year, month: t.month, day: t.day };
    if (mode === 'datetime') {
      const now = toSegs({ ...t, minute: t.minute - (t.minute % minuteStep) });
      next = { ...next, hour: now.hour, minute: now.minute, ampm: now.ampm };
    }
    commit(next);
  };
  const clearValue = () => {
    commit(Object.fromEntries(order.map(k => [k, null])));
    closeCalendar();
  };

  const shiftView = (dir) => setView(v => {
    const m = v.month + dir;
    return m < 1 ? { year: v.year - 1, month: 12 } : m > 12 ? { year: v.year + 1, month: 1 } : { year: v.year, month: m };
  });

  const calendarCells = () => {
    const first = new Date(view.year, view.month - 1, 1).getDay();
    const start = new Date(view.year, view.month - 1, 1 - first);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
    });
  };

  const today = nowParts(displayZone);
  const same = (a, b) => a.year === b.year && a.month === b.month && a.day === b.day;

  return (
    <div
      ref={boxRef}
      className={`stui-datetime${disabled ? ' disabled' : ''}${dirty ? ' dirty' : ''}${compact ? ' compact' : ''}`}
      title={title}
      onMouseDown={e => { if (e.target === boxRef.current) { e.preventDefault(); focusSeg(order[0]); } }}
    >
      {order.map((key, i) => (
        <React.Fragment key={key}>
          {mode === 'datetime' && key === 'hour' && <span className="stui-datetime-sep">&nbsp;</span>}
          <span
            ref={el => { segRefs.current[key] = el; }}
            className={`stui-datetime-seg${segs[key] == null ? ' empty' : ''}`}
            tabIndex={disabled ? -1 : 0}
            role="spinbutton"
            aria-label={key}
            aria-valuenow={typeof segs[key] === 'number' ? segs[key] : undefined}
            onFocus={() => { activeSeg.current = key; typed.current = { key: null, buf: '' }; }}
            onKeyDown={e => onKeyDown(key, e)}
          >
            {segText(key, segs[key])}
          </span>
          {SEPARATOR_AFTER[key] && order[i + 1] && <span className="stui-datetime-sep">{SEPARATOR_AFTER[key]}</span>}
        </React.Fragment>
      ))}
      {showZone && mode !== 'date' && <span className="stui-datetime-zone">{zoneAbbrev(new Date(), displayZone)}</span>}
      <span className="stui-datetime-spin">
        <button type="button" tabIndex={-1} disabled={disabled} title="Up" onMouseDown={e => { e.preventDefault(); step(activeSeg.current || spinDefault, 1); }}>
          <ChevronUp size={12} />
        </button>
        <button type="button" tabIndex={-1} disabled={disabled} title="Down" onMouseDown={e => { e.preventDefault(); step(activeSeg.current || spinDefault, -1); }}>
          <ChevronDown size={12} />
        </button>
      </span>
      {hasDate && (
        <button type="button" tabIndex={-1} disabled={disabled} title="Pick a date" onClick={() => (calendarAt ? closeCalendar() : openCalendar())}>
          <CalendarDays size={16} />
        </button>
      )}

      {calendarAt && view && createPortal(
        <div ref={calRef} className="stui-calendar" style={{ top: calendarAt.top, left: calendarAt.left }}>
          <div className="stui-calendar-header">
            <button type="button" onClick={() => shiftView(-1)} title="Previous month"><ChevronLeft size={16} /></button>
            <span>{MONTH_NAMES[view.month - 1]} {view.year}</span>
            <button type="button" onClick={() => shiftView(1)} title="Next month"><ChevronRight size={16} /></button>
          </div>
          <div className="stui-calendar-grid">
            {WEEKDAY_LETTERS.map((w, i) => <span key={i}>{w}</span>)}
            {calendarCells().map(c => {
              const selected = segs.year && segs.month && segs.day && same(c, segs);
              const cls = ['stui-calendar-day',
                c.month !== view.month && 'outside', same(c, today) && 'today', selected && 'selected']
                .filter(Boolean).join(' ');
              return (
                <button type="button" key={`${c.year}-${c.month}-${c.day}`} className={cls} onClick={() => pickDay(c.year, c.month, c.day)}>
                  {c.day}
                </button>
              );
            })}
          </div>
          <div className="stui-calendar-footer">
            <button type="button" onClick={clearValue}>Clear</button>
            <button type="button" onClick={pickToday}>Today</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default StuiDateTime;
